import { identity, isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReplaySubject } from 'rxjs';
import { useEndpointsContext } from '../../contexts';
import { useGlobalToasts } from '../../hooks/Toast/useGlobalToasts';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { useConstant } from '../../hooks/useConstant';
import { useObservable } from '../../hooks/useObservable';
import type { MinimalSubscriptionResponse } from '../../types';
import { envConfig } from '../../utils/envConfig';
import { request as sendRequest } from '../../utils/http';
import { logger } from '../../utils/logger';
import { NotificationVariants } from '../Notification';
import { DEFAULT_MAX_ROWS } from './helpers';
import type {
  BlotterTableFilter,
  ColumnOrColumnGroup,
  CompositePipeFunction,
  InitialFetchState,
  RestBlotterPaginationLimit,
} from './types';
import { useBlotterTable } from './useBlotterTable';
import type { UseBlotterTable, UseBlotterTableProps } from './useBlotterTable/types';
import type { UsePersistedBlotterTable } from './usePersistedBlotterTable';

interface RestBlotterRequest {
  baseUrl?: string;
  path: string;
  filters?: any;
  sortBy?: string;
  limit?: number;
  next?: string;
}

export type UseRestBlotterTableProps<
  TRowOutputType extends object,
  TRowInputType extends object = TRowOutputType,
> = Omit<UseBlotterTableProps<TRowOutputType>, 'dataObservable' | 'errorObservable' | 'persistence'> & {
  rowID: string;
  /**
   * Pass `null` to defer fetching and ignore responses from in-flight requests.
   * Existing rows remain visible.
   *
   * Fetches on mount, after `null` becomes a request, and when filters change.
   * Other request changes require an explicit `refresh()`.
   */
  request: RestBlotterRequest | null;
  blotterRowCount?: number;
  filter?: BlotterTableFilter;
  pipe?: CompositePipeFunction<TRowOutputType, TRowInputType>;
  persistence?: UsePersistedBlotterTable;
  queryParams?: Record<string, string>;
  suppressLoadingUntilFetched?: boolean;
};

export function useRestBlotterTable<TRowOutputType extends object, TRowInputType extends object = TRowOutputType>({
  request,
  rowID,
  columns,
  blotterRowCount = DEFAULT_MAX_ROWS,
  filter,
  pipe: convertInputToOutputPipe = identity,
  queryParams,
  suppressLoadingUntilFetched,
  ...props
}: UseRestBlotterTableProps<TRowOutputType, TRowInputType>): UseBlotterTable<TRowOutputType> & {
  /**
   * Refreshes the Rest API call. If force is true, it will clear the blotter AND make a new request.
   * We need to clear the blotter when deleting a row, for example.
   */
  refresh: (force?: boolean) => void;
  /**
   * Sends a single record update to the blotter table without refetching the entire table.
   */
  updateRecord: (record: TRowInputType) => void;
  /**
   * Pagination information for load all functionality
   */
  paginationLimit: RestBlotterPaginationLimit;
  /** Tracks whether the initial data fetch has completed, and how many records have been loaded. */
  initialFetchState: InitialFetchState;
} {
  const { orgApiEndpoint } = useEndpointsContext();
  const baseUrl = request?.baseUrl ?? orgApiEndpoint;
  const { add: addToast, remove: removeToast } = useGlobalToasts();

  const [recordsReceived, setRecordsReceived] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Track the effective limit - starts as blotterRowCount, can be expanded to Number.MAX_SAFE_INTEGER via loadAll
  const [effectiveLimit, setEffectiveLimit] = useState<number>(blotterRowCount);

  const initialFetchCompleteRef = useRef(false);
  const [initialFetchState, setInitialFetchState] = useState<InitialFetchState>({
    isComplete: false,
    loadedCount: 0,
  });

  const dataSubject = useConstant(new ReplaySubject<MinimalSubscriptionResponse<TRowInputType>>(1));
  const dataObservable = useObservable(() => dataSubject.asObservable(), [dataSubject]);
  const pipedSubscription = useObservable(
    () => dataObservable.pipe(convertInputToOutputPipe),
    [dataObservable, convertInputToOutputPipe]
  );

  const requestIdRef = useRef(0);

  const activeRequest = useMemo(() => (request == null ? null : { ...request, filters: filter }), [request, filter]);
  const [activeColumns, setActiveColumns] = useState<ColumnOrColumnGroup[]>(columns);
  useEffect(() => {
    if (envConfig.VITE_AVA_ENV !== 'local') {
      // Allow columns to update from react-fast-refresh
      return;
    }
    setActiveColumns(columns);
  }, [columns]);

  const queryApi = useCallback(
    async (req: RestBlotterRequest, overrideLimit?: number) => {
      const requestId = ++requestIdRef.current;
      const query = {
        ...req.filters,
      };
      if (req.limit) {
        query.limit = req.limit;
      }
      if (req.next) {
        query.after = req.next;
      }
      if (req.sortBy) {
        query.orderBy = req.sortBy;
      }
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          query[key] = value;
        });
      }
      const queryString = new URLSearchParams(query).toString();
      const url = queryString ? `${baseUrl}${req.path}?${queryString}` : `${baseUrl}${req.path}`;
      // Paginate until we fit the effective limit. This is a "single page" of the blotter table
      // Use overrideLimit if provided, otherwise use the effective limit (which may have been expanded via loadAll)
      const limitToUse = overrideLimit ?? effectiveLimit;
      return sendRequest<MinimalSubscriptionResponse<TRowInputType>>('GET', url, null, {
        paginateRecords: limitToUse,
        onPageData: (cumulativeData: unknown[]) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          const typedData = cumulativeData as TRowInputType[];
          if (suppressLoadingUntilFetched) {
            if (!initialFetchCompleteRef.current) {
              initialFetchCompleteRef.current = true;
              setInitialFetchState({ isComplete: true, loadedCount: typedData.length });
            }
            dataSubject.next({ data: typedData, initial: true });
          } else if (!initialFetchCompleteRef.current) {
            setInitialFetchState({ isComplete: false, loadedCount: typedData.length });
          }
        },
      })
        .then(response => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setRecordsReceived(response.data?.length ?? 0);
          setLimitReached(response.next != null);
          if (!initialFetchCompleteRef.current) {
            initialFetchCompleteRef.current = true;
            setInitialFetchState({ isComplete: true, loadedCount: response.data?.length ?? 0 });
          }
          dataSubject.next(response);
        })
        .catch(err => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          removeToast('rest-blotter-table-error');
          addToast({
            text: err?.toString() ?? 'Check path: Could not fetch.',
            variant: NotificationVariants.Negative,
            id: 'rest-blotter-table-error',
          });
          setRecordsReceived(0);
          setLimitReached(false);
          if (!initialFetchCompleteRef.current) {
            initialFetchCompleteRef.current = true;
            setInitialFetchState({ isComplete: true, loadedCount: 0 });
          }
          dataSubject.next({ data: [], initial: true });
        });
    },
    [queryParams, baseUrl, effectiveLimit, dataSubject, removeToast, addToast, suppressLoadingUntilFetched]
  );

  const loadAll = useCallback(() => {
    if (activeRequest == null) {
      return;
    }
    setIsLoading(true);
    setLimitReached(false);
    // Persist the expanded limit so subsequent refreshes maintain the full dataset
    setEffectiveLimit(Number.MAX_SAFE_INTEGER);
    // Fetch all records by using a very large limit
    queryApi({ ...activeRequest, filters: filter }, Number.MAX_SAFE_INTEGER)
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setLimitReached(true);
        logger.warn('Failed to load all records in useRestBlotterTable', {
          blotterRowCount,
          activeRequest,
          filter,
        });
        setIsLoading(false);
      });
  }, [activeRequest, filter, queryApi, blotterRowCount]);

  const refresh = useCallbackRef((force?: boolean) => {
    if (activeRequest == null) {
      return;
    }
    force && dataSubject.next({ data: [], initial: true });
    queryApi(activeRequest);
  });

  const updateRecord = useCallback(
    (record: TRowInputType) => dataSubject.next({ data: [record], initial: false }),
    [dataSubject]
  );

  const hasFetchedRef = useRef(false);
  const lastFetchedFilterRef = useRef<BlotterTableFilter | undefined>(filter);

  useEffect(() => {
    if (activeRequest == null) {
      requestIdRef.current += 1;
      hasFetchedRef.current = false;
      return;
    }
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      lastFetchedFilterRef.current = filter;
      refresh();
      return;
    }
    if (!isEqual(lastFetchedFilterRef.current, filter)) {
      lastFetchedFilterRef.current = filter;
      refresh(true);
    }
  }, [activeRequest, filter, refresh]);

  return {
    ...useBlotterTable<TRowOutputType>({
      dataObservable: pipedSubscription,
      errorObservable: undefined,
      rowID,
      columns: activeColumns,
      ...props,
      //TODO: Change the blotter table props to align with the useBlotterTable props
      // - the Input/Output RowType generic used with persistence does not (yet) line up with the UseBlotterTableProps types
    } as UseBlotterTableProps<TRowOutputType>),
    refresh,
    updateRecord,
    initialFetchState,
    paginationLimit: {
      limitReached,
      recordsReceived,
      currentLimit: effectiveLimit,
      loadAll,
      isLoading,
    },
  };
}
